<template>
  <v-dialog
    persistent
    :value="true"
    max-width="560"
    data-testid="signatureDialog-dialog-root"
  >
    <v-card class="px-6 py-5" data-testid="signatureDialog-card-container">
      <v-card-title class="pa-0" data-testid="signatureDialog-text-title">
        {{ title }}
      </v-card-title>

      <v-card-text
        class="pa-0 mt-3"
        data-testid="signatureDialog-section-content"
      >
        <p
          class="mb-0 text-body-2 blue-grey--text text--darken-1"
          data-testid="signatureDialog-text-subtitle"
        >
          {{ subtitle }}
        </p>

        <div
          class="signature-dialog__pad mt-4"
          data-testid="signatureDialog-section-pad"
        >
          <VueSignaturePad
            v-if="shouldRenderPad"
            ref="signaturePad"
            :width="padWidth"
            :height="padHeight"
            :options="signaturePadOptions"
            class="signature-dialog__padCanvas"
            data-testid="signatureDialog-pad-canvas"
          />
        </div>

        <div
          class="signature-dialog__signer mt-2 font-weight-medium text-center body-2"
          data-testid="signatureDialog-text-signerName"
        >
          {{ signerName }}
        </div>
      </v-card-text>

      <v-card-actions
        class="pa-0 mt-5 justify-space-between"
        data-testid="signatureDialog-section-actions"
      >
        <v-btn
          color="primary"
          data-testid="signatureDialog-button-clear"
          outlined
          :disabled="isUploading"
          @click="handleClear"
        >
          {{ $t('common.retry') }}
        </v-btn>

        <div
          class="d-flex"
          data-testid="signatureDialog-section-primaryActions"
        >
          <Button
            color="grey"
            data-testid="signatureDialog-button-cancel"
            plain
            :disabled="isUploading"
            :text="$t('common.button.cancel')"
            @click="$emit('close')"
          />

          <Button
            class="ml-3"
            color="primary"
            data-testid="signatureDialog-button-saveSignature"
            :disabled="disableSaveButton"
            :loading="isUploading"
            :text="$t('common.done')"
            @click="handleSave"
          />
        </div>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters } from 'vuex'
import { VueSignaturePad } from 'vue-signature-pad'

import ReverMediaSDK from '@/lib/ReverMediaSDK'
import mixin from '@/mixins/mixin.vue'
import Button from '@/components/UI/Button.vue'

const dataUrlToBlob = dataUrl => {
  const [header, content = ''] = dataUrl.split(',')
  const mimeType = header?.match(/data:(.*?);base64/)?.[1] ?? 'image/png'
  const binary = globalThis.atob(content)
  const bytes = new Uint8Array(binary.length)

  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.codePointAt(index)
  }

  return new Blob([bytes], { type: mimeType })
}

export default {
  name: 'SignatureDialog',

  components: {
    Button,
    VueSignaturePad,
  },

  mixins: [mixin],

  props: {
    title: {
      type: String,
      required: true,
    },
    subtitle: {
      type: String,
      required: true,
    },
    signerName: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      hasSignature: false,
      isUploading: false,
      signaturePad: null,
      shouldRenderPad: false,
      padWidth: '100%',
      padHeight: '220px',
    }
  },

  computed: {
    ...mapGetters(['account']),

    disableSaveButton() {
      return this.isUploading || !this.hasSignature
    },

    signaturePadOptions() {
      return {
        minWidth: 1.2,
        maxWidth: 2.4,
        throttle: 12,
        minDistance: 2,
        onBegin: this.handleSignatureBegin,
        onEnd: this.handleSignatureEnd,
      }
    },
  },

  mounted() {
    this.mountSignaturePad()
  },

  methods: {
    resetPadRender() {
      this.shouldRenderPad = false
      this.signaturePad = null
      this.hasSignature = false
      this.padWidth = '100%'
      this.padHeight = '220px'
    },

    mountSignaturePad() {
      this.$nextTick(() => {
        this.shouldRenderPad = true

        this.$nextTick(async () => {
          this.signaturePad = this.$refs.signaturePad ?? null
          await this.resizeSignaturePad()
        })
      })
    },

    async resizeSignaturePad() {
      this.signaturePad?.resizeCanvas?.()
    },

    handleSignatureBegin() {
      this.hasSignature = true
    },

    handleSignatureEnd() {
      this.hasSignature = !this.signaturePad?.isEmpty?.()
    },

    handleClear() {
      if (this.isUploading) return

      this.signaturePad?.clearSignature?.()
      this.hasSignature = false
    },

    async handleSave() {
      if (this.disableSaveButton) return

      const signature = this.signaturePad?.saveSignature?.('image/png')

      if (signature?.isEmpty || !signature?.data) return

      this.isUploading = true

      try {
        const media = await ReverMediaSDK.uploadMedia(
          dataUrlToBlob(signature.data)
        )

        this.$emit('save', {
          media,
          mediaId: media.id,
          mediaUrl: media.url,
          signerId: this.account?.id ?? null,
          signerName: this.signerName,
        })
        this.$emit('close')
      } catch (error) {
        this.handleRequestError(error)
      } finally {
        this.isUploading = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.signature-dialog__pad {
  width: 100%;
  height: 220px;
  border: 1px solid $color-blue-grey;
  border-radius: 4px;
  overflow: hidden;
}

.signature-dialog__padCanvas {
  display: block;
}
</style>
